import { LUCK_COEF } from '../data/constants';
import type {
  AttackSkill,
  BlockSkill,
  BuffSkill,
  CombatantState,
  RepairSkill,
  Skill,
  SkillRegistry,
} from './types';

// Effective trigger chance includes Luck bonus.
export function computeSkillChance(baseChance: number, luck: number): number {
  return baseChance + luck * LUCK_COEF;
}

function getSkills(actor: CombatantState, registry: SkillRegistry): Skill[] {
  const out: Skill[] = [];
  for (const id of actor.config.skillIds) {
    const skill = registry.get(id);
    if (skill) out.push(skill);
  }
  return out;
}

// Walks actor's skills in order, returns the first one that rolls true.
// rng() is called at most once per skill checked.
export function resolveAttackSkill(
  actor: CombatantState,
  registry: SkillRegistry,
  rng: () => number,
): AttackSkill | null {
  const luck = actor.config.stats.luck;
  for (const skill of getSkills(actor, registry)) {
    if (skill.kind !== 'attack') continue;
    if (rng() < computeSkillChance(skill.triggerChance, luck)) {
      return skill;
    }
  }
  return null;
}

export function resolveBlockSkill(
  actor: CombatantState,
  registry: SkillRegistry,
  rng: () => number,
): BlockSkill | null {
  const luck = actor.config.stats.luck;
  for (const skill of getSkills(actor, registry)) {
    if (skill.kind !== 'block') continue;
    if (rng() < computeSkillChance(skill.triggerChance, luck)) {
      return skill;
    }
  }
  return null;
}

// Rolled at the START of an actor's turn (after repair, before attack), and
// also for a defender on a successful dodge (before the counter). First buff
// skill that rolls true wins; rng() once per buff skill until the first
// success. Actors with no buff skills consume no rng (determinism).
export function resolveBuffSkill(
  actor: CombatantState,
  registry: SkillRegistry,
  rng: () => number,
): BuffSkill | null {
  const luck = actor.config.stats.luck;
  for (const skill of getSkills(actor, registry)) {
    if (skill.kind !== 'buff') continue;
    if (rng() < computeSkillChance(skill.triggerChance, luck)) {
      return skill;
    }
  }
  return null;
}

// Rolled at the START of an actor's turn, before attack resolution. If a repair
// skill triggers the actor heals instead of attacking. rng() once per repair
// skill until the first success (same "first that rolls wins" rule).
export function resolveRepairSkill(
  actor: CombatantState,
  registry: SkillRegistry,
  rng: () => number,
): RepairSkill | null {
  const luck = actor.config.stats.luck;
  for (const skill of getSkills(actor, registry)) {
    if (skill.kind !== 'repair') continue;
    if (rng() < computeSkillChance(skill.triggerChance, luck)) {
      return skill;
    }
  }
  return null;
}
